import { createStyles, Stack } from '@mantine/core';
import type { EmptyStateButtons } from '@repo/common/components/NewEmptyState/EmptyState';
import { EmptyState } from '@repo/common/components/NewEmptyState/EmptyState';
import { UserRole } from '@repo/common/enums/UserRole';
import { useMemo } from 'react';
import { Navigate } from 'react-router';
import { useAuthUser } from '../../api';
import { useAutomationList } from '../../api/hooks/automation';
import { useSuspenseFeatureAccess } from '../../api/hooks/workspace/useSuspenseFeatureAccess';
import AddAutomationModal from '../../components/Automation/AddAutomationModal';
import { EducationModal } from '../../components/EducationModal/EducationModal';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import { FullWidthLoadingSpinner } from '../../components/LoadingSpinner';
import { closeAllModals, openModal } from '../../components/ModalManager';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { useFeatureFlags } from '../../utils/featureFlags';
import { useStyles as useTeamCatalogPageStyles } from '../TeamCatalogPage/TeamCatalogPage.styles';
import AutomationList from './AutomationList';

const useStyles = createStyles({
	modalContent: {
		overflow: 'hidden',
		padding: 0,
	},
});

function AutomationsPage() {
	const { classes } = useTeamCatalogPageStyles();
	const { classes: modalClasses } = useStyles();

	const { workspace } = useAuthUser();

	const { automationAiGeneration } = useFeatureFlags();

	const { automationsAccess } = useSuspenseFeatureAccess();
	const { hasPermission: canCreateAutomation } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'Automations.Create',
	});

	const { data: automations, isLoading } = useAutomationList({});

	const buttons = useMemo(() => {
		const baseButtons = [];
		baseButtons.push({
			label: 'Learn more',
			variant: 'default',
			onClick: () => {
				window.open('https://docs.secoda.com/features/automations', '_blank');
			},
		});

		if (automationsAccess && canCreateAutomation) {
			baseButtons.push({
				label: 'Create automation',
				variant: 'primary',
				onClick: () => {
					openModal({
						variant: 'default',
						title: 'New automation',
						children: <AddAutomationModal onClose={() => closeAllModals()} />,
						lockScroll: true,
						size: 640,
						padding: 0,
						classNames: {
							body: modalClasses.modalContent,
						},
					});
				},
			});
		}
		return baseButtons;
	}, [automationsAccess, canCreateAutomation, modalClasses.modalContent]);

	if (!workspace.automation_enabled) {
		return <Navigate to="/" />;
	}

	if (isLoading) {
		return <FullWidthLoadingSpinner />;
	}

	if (automations && automations.results.length === 0) {
		return (
			<EmptyState
				heading="Automate your data documentation"
				body="Automatically set tags, owners, property values, and so much more with automations."
				illustrationName="automations"
				buttons={buttons as EmptyStateButtons}
			/>
		);
	}

	return (
		<FeatureGuard
			v1AllowedRoles={[UserRole.ADMIN, UserRole.EDITOR]}
			v2Permission="Automations.Read"
			isFeatureIncludedInPlan={automationsAccess ?? false}
			isAlwaysIncludedFeature={false}
			description="Automatically set tags, owners, property values, and so much more with automations."
			featureName="Automation"
		>
			<Stack className={classes.featureWrapper}>
				<AutomationList />
			</Stack>
			{automationAiGeneration && (
				<EducationModal modalKey="education-modal-of-secoda-automations" />
			)}
		</FeatureGuard>
	);
}

export default AutomationsPage;
